import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LogOut, Menu, Save, Plus, Trash2, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import AdminSidebar from '../../components/AdminSidebar';
import RichTextEditor from '../../components/RichTextEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const defaultAbout = {
  title: "About Indicraft",
  subtitle: "Your trusted partner for premium quality products",
  story_title: "Our Story",
  story_content: "<p>Founded in 2026, Indicraft was born from a simple idea: to make premium quality products accessible to everyone across India.</p>",
  mission_title: "Our Mission",
  mission_content: "To provide premium quality products at affordable prices, with exceptional customer service and fast delivery across India.",
  vision_title: "Our Vision",
  vision_content: "To become India's most trusted e-commerce platform, known for quality products, transparent pricing, and customer-first approach.",
  features: [
    { icon: "Award", title: "Premium Quality", description: "Every product is carefully selected and quality-tested before listing." },
    { icon: "TrendingUp", title: "Best Prices", description: "Unbeatable prices with regular discounts and special offers." },
    { icon: "Users", title: "Customer First", description: "Your satisfaction is our priority. 24/7 customer support available." },
    { icon: "Truck", title: "Fast Delivery", description: "Quick and reliable delivery across India with order tracking." }
  ],
  cta_title: "Join Thousands of Happy Customers",
  cta_subtitle: "Experience the difference of shopping with Indicraft today",
  cta_button_text: "Start Shopping",
  meta_title: "About Us | Indicraft",
  meta_description: "Learn about Indicraft - your trusted partner for premium quality Indian handicrafts."
};

const defaultContact = {
  title: "Get In Touch",
  subtitle: "Have a question? We'd love to hear from you.",
  form_title: "Send Us a Message",
  contact_email: "support@indicraft.in",
  contact_phone: "+91 123-456-7890",
  address_line1: "123 Business Park",
  address_line2: "Mumbai, Maharashtra 400001",
  address_line3: "India",
  whatsapp_number: "+911234567890",
  whatsapp_message: "Hi, I have a question about...",
  business_hours: "Monday - Saturday: 9:00 AM - 7:00 PM\nSunday: 10:00 AM - 5:00 PM",
  meta_title: "Contact Us | Indicraft",
  meta_description: "Contact Indicraft for any queries about orders, products, or support."
};

const defaultFAQ = {
  title: "Frequently Asked Questions",
  subtitle: "Find answers to common questions about ordering, shipping, and more",
  categories: [
    {
      category: "Orders & Shipping",
      questions: [
        { q: "How long does shipping take?", a: "We deliver within 5-7 business days across India." },
        { q: "Do you offer free shipping?", a: "Yes! We offer free shipping on all orders across India." }
      ]
    },
    {
      category: "Payment",
      questions: [
        { q: "Is Cash on Delivery available?", a: "Yes! COD is available for all orders." },
        { q: "What payment methods do you accept?", a: "We accept UPI, Cards, Net Banking, and COD." }
      ]
    }
  ],
  cta_title: "Still Have Questions?",
  cta_subtitle: "Our customer support team is here to help!",
  cta_button_text: "Contact Support",
  meta_title: "FAQ | Indicraft",
  meta_description: "Find answers to frequently asked questions about Indicraft."
};

const iconOptions = ["Award", "TrendingUp", "Users", "Truck", "ShieldCheck", "Target"];

const AdminPages = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('about');
  
  const [aboutContent, setAboutContent] = useState(defaultAbout);
  const [contactContent, setContactContent] = useState(defaultContact);
  const [faqContent, setFaqContent] = useState(defaultFAQ);

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('admin_logged_in');
    if (isLoggedIn !== 'true') {
      navigate('/admin', { replace: true });
      return;
    }
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const [aboutRes, contactRes, faqRes] = await Promise.all([
        axios.get(`${API_URL}/api/cms/about`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/cms/contact`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/cms/faq`).catch(() => ({ data: null }))
      ]);
      
      if (aboutRes.data) setAboutContent({ ...defaultAbout, ...aboutRes.data });
      if (contactRes.data) setContactContent({ ...defaultContact, ...contactRes.data });
      if (faqRes.data) setFaqContent({ ...defaultFAQ, ...faqRes.data });
    } catch (error) {
      console.log('Using default content');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_logged_in');
    localStorage.removeItem('admin_username');
    navigate('/admin', { replace: true });
  };

  const handleSaveAbout = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/admin/cms/about`, aboutContent);
      toast.success('About page updated successfully');
    } catch (error) {
      toast.error('Failed to update About page');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveContact = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/admin/cms/contact`, contactContent);
      toast.success('Contact page updated successfully');
    } catch (error) {
      toast.error('Failed to update Contact page');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFAQ = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/admin/cms/faq`, faqContent);
      toast.success('FAQ page updated successfully');
    } catch (error) {
      toast.error('Failed to update FAQ page');
    } finally {
      setLoading(false);
    }
  };

  const addFeature = () => {
    setAboutContent(prev => ({
      ...prev,
      features: [...prev.features, { icon: "Award", title: "", description: "" }]
    }));
  };

  const removeFeature = (index) => {
    setAboutContent(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const updateFeature = (index, field, value) => {
    setAboutContent(prev => ({
      ...prev,
      features: prev.features.map((f, i) => i === index ? { ...f, [field]: value } : f)
    }));
  };

  const addFAQCategory = () => {
    setFaqContent(prev => ({
      ...prev,
      categories: [...prev.categories, { category: "New Category", questions: [] }]
    }));
  };

  const removeFAQCategory = (catIndex) => {
    setFaqContent(prev => ({
      ...prev,
      categories: prev.categories.filter((_, i) => i !== catIndex)
    }));
  };

  const updateFAQCategory = (catIndex, value) => {
    setFaqContent(prev => ({
      ...prev,
      categories: prev.categories.map((c, i) => i === catIndex ? { ...c, category: value } : c)
    }));
  };

  const addFAQQuestion = (catIndex) => {
    setFaqContent(prev => ({
      ...prev,
      categories: prev.categories.map((c, i) => 
        i === catIndex ? { ...c, questions: [...c.questions, { q: "", a: "" }] } : c
      )
    }));
  };

  const removeFAQQuestion = (catIndex, qIndex) => {
    setFaqContent(prev => ({
      ...prev,
      categories: prev.categories.map((c, i) => 
        i === catIndex ? { ...c, questions: c.questions.filter((_, j) => j !== qIndex) } : c
      )
    }));
  };

  const updateFAQQuestion = (catIndex, qIndex, field, value) => {
    setFaqContent(prev => ({
      ...prev,
      categories: prev.categories.map((c, i) => 
        i === catIndex ? {
          ...c,
          questions: c.questions.map((q, j) => j === qIndex ? { ...q, [field]: value } : q)
        } : c
      )
    }));
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <AdminSidebar active="pages" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="admin-content">
        <div className="admin-topbar">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden">
              <Menu className="w-6 h-6" />
            </Button>
            <h1 className="text-xl font-bold text-[#0f172a]" data-testid="pages-title">Page Management</h1>
          </div>
          <Button data-testid="logout-button" onClick={handleLogout} variant="ghost" className="text-zinc-600">
            <LogOut className="w-5 h-5 mr-2" />
            Logout
          </Button>
        </div>

        <div className="p-6">
          <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-6">
                <TabsTrigger value="about">About Page</TabsTrigger>
                <TabsTrigger value="contact">Contact Page</TabsTrigger>
                <TabsTrigger value="faq">FAQ Page</TabsTrigger>
              </TabsList>

              {/* About Page Tab */}
              <TabsContent value="about" className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <Label>Page Title</Label>
                    <Input value={aboutContent.title} onChange={(e) => setAboutContent(p => ({ ...p, title: e.target.value }))} className="mt-1" />
                  </div>
                  <div>
                    <Label>Subtitle</Label>
                    <Input value={aboutContent.subtitle} onChange={(e) => setAboutContent(p => ({ ...p, subtitle: e.target.value }))} className="mt-1" />
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <Label>SEO Meta Title</Label>
                    <Input value={aboutContent.meta_title} onChange={(e) => setAboutContent(p => ({ ...p, meta_title: e.target.value }))} className="mt-1" />
                  </div>
                  <div>
                    <Label>SEO Meta Description</Label>
                    <Input value={aboutContent.meta_description} onChange={(e) => setAboutContent(p => ({ ...p, meta_description: e.target.value }))} className="mt-1" />
                  </div>
                </div>

                <div>
                  <Label>Story Section Title</Label>
                  <Input value={aboutContent.story_title} onChange={(e) => setAboutContent(p => ({ ...p, story_title: e.target.value }))} className="mt-1" />
                </div>
                <div>
                  <Label>Story Content (Rich Text)</Label>
                  <div className="mt-2 border rounded-lg">
                    <RichTextEditor value={aboutContent.story_content} onChange={(v) => setAboutContent(p => ({ ...p, story_content: v }))} />
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <Label>Mission Title</Label>
                    <Input value={aboutContent.mission_title} onChange={(e) => setAboutContent(p => ({ ...p, mission_title: e.target.value }))} className="mt-1" />
                  </div>
                  <div>
                    <Label>Vision Title</Label>
                    <Input value={aboutContent.vision_title} onChange={(e) => setAboutContent(p => ({ ...p, vision_title: e.target.value }))} className="mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <Label>Mission Content</Label>
                    <Textarea value={aboutContent.mission_content} onChange={(e) => setAboutContent(p => ({ ...p, mission_content: e.target.value }))} rows={3} className="mt-1" />
                  </div>
                  <div>
                    <Label>Vision Content</Label>
                    <Textarea value={aboutContent.vision_content} onChange={(e) => setAboutContent(p => ({ ...p, vision_content: e.target.value }))} rows={3} className="mt-1" />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label>Features (Why Choose Us)</Label>
                    <Button onClick={addFeature} size="sm" variant="outline"><Plus className="w-4 h-4 mr-1" /> Add Feature</Button>
                  </div>
                  <div className="space-y-3">
                    {aboutContent.features.map((feature, idx) => (
                      <div key={idx} className="flex gap-3 items-start p-3 bg-zinc-50 rounded-lg">
                        <select value={feature.icon} onChange={(e) => updateFeature(idx, 'icon', e.target.value)} className="border rounded px-2 py-1 text-sm">
                          {iconOptions.map(icon => <option key={icon} value={icon}>{icon}</option>)}
                        </select>
                        <Input placeholder="Title" value={feature.title} onChange={(e) => updateFeature(idx, 'title', e.target.value)} className="flex-1" />
                        <Input placeholder="Description" value={feature.description} onChange={(e) => updateFeature(idx, 'description', e.target.value)} className="flex-1" />
                        <Button onClick={() => removeFeature(idx)} size="sm" variant="ghost" className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div><Label>CTA Title</Label><Input value={aboutContent.cta_title} onChange={(e) => setAboutContent(p => ({ ...p, cta_title: e.target.value }))} className="mt-1" /></div>
                  <div><Label>CTA Subtitle</Label><Input value={aboutContent.cta_subtitle} onChange={(e) => setAboutContent(p => ({ ...p, cta_subtitle: e.target.value }))} className="mt-1" /></div>
                  <div><Label>CTA Button Text</Label><Input value={aboutContent.cta_button_text} onChange={(e) => setAboutContent(p => ({ ...p, cta_button_text: e.target.value }))} className="mt-1" /></div>
                </div>

                <Button onClick={handleSaveAbout} disabled={loading} className="bg-[#064E3B] hover:bg-[#065F46]">
                  <Save className="w-4 h-4 mr-2" />{loading ? 'Saving...' : 'Save About Page'}
                </Button>
              </TabsContent>

              {/* Contact Page Tab */}
              <TabsContent value="contact" className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div><Label>Page Title</Label><Input value={contactContent.title} onChange={(e) => setContactContent(p => ({ ...p, title: e.target.value }))} className="mt-1" /></div>
                  <div><Label>Form Title</Label><Input value={contactContent.form_title} onChange={(e) => setContactContent(p => ({ ...p, form_title: e.target.value }))} className="mt-1" /></div>
                </div>
                <div><Label>Subtitle</Label><Input value={contactContent.subtitle} onChange={(e) => setContactContent(p => ({ ...p, subtitle: e.target.value }))} className="mt-1" /></div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div><Label>SEO Meta Title</Label><Input value={contactContent.meta_title} onChange={(e) => setContactContent(p => ({ ...p, meta_title: e.target.value }))} className="mt-1" /></div>
                  <div><Label>SEO Meta Description</Label><Input value={contactContent.meta_description} onChange={(e) => setContactContent(p => ({ ...p, meta_description: e.target.value }))} className="mt-1" /></div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div><Label>Contact Email</Label><Input value={contactContent.contact_email} onChange={(e) => setContactContent(p => ({ ...p, contact_email: e.target.value }))} className="mt-1" /></div>
                  <div><Label>Contact Phone</Label><Input value={contactContent.contact_phone} onChange={(e) => setContactContent(p => ({ ...p, contact_phone: e.target.value }))} className="mt-1" /></div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div><Label>Address Line 1</Label><Input value={contactContent.address_line1} onChange={(e) => setContactContent(p => ({ ...p, address_line1: e.target.value }))} className="mt-1" /></div>
                  <div><Label>Address Line 2</Label><Input value={contactContent.address_line2} onChange={(e) => setContactContent(p => ({ ...p, address_line2: e.target.value }))} className="mt-1" /></div>
                  <div><Label>Address Line 3</Label><Input value={contactContent.address_line3} onChange={(e) => setContactContent(p => ({ ...p, address_line3: e.target.value }))} className="mt-1" /></div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div><Label>WhatsApp Number</Label><Input value={contactContent.whatsapp_number} onChange={(e) => setContactContent(p => ({ ...p, whatsapp_number: e.target.value }))} className="mt-1" placeholder="+911234567890" /></div>
                  <div><Label>WhatsApp Default Message</Label><Input value={contactContent.whatsapp_message} onChange={(e) => setContactContent(p => ({ ...p, whatsapp_message: e.target.value }))} className="mt-1" /></div>
                </div>

                <div>
                  <Label>Business Hours</Label>
                  <Textarea value={contactContent.business_hours} onChange={(e) => setContactContent(p => ({ ...p, business_hours: e.target.value }))} rows={3} className="mt-1" />
                </div>

                <Button onClick={handleSaveContact} disabled={loading} className="bg-[#064E3B] hover:bg-[#065F46]">
                  <Save className="w-4 h-4 mr-2" />{loading ? 'Saving...' : 'Save Contact Page'}
                </Button>
              </TabsContent>

              {/* FAQ Page Tab */}
              <TabsContent value="faq" className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div><Label>Page Title</Label><Input value={faqContent.title} onChange={(e) => setFaqContent(p => ({ ...p, title: e.target.value }))} className="mt-1" /></div>
                  <div><Label>Subtitle</Label><Input value={faqContent.subtitle} onChange={(e) => setFaqContent(p => ({ ...p, subtitle: e.target.value }))} className="mt-1" /></div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div><Label>SEO Meta Title</Label><Input value={faqContent.meta_title} onChange={(e) => setFaqContent(p => ({ ...p, meta_title: e.target.value }))} className="mt-1" /></div>
                  <div><Label>SEO Meta Description</Label><Input value={faqContent.meta_description} onChange={(e) => setFaqContent(p => ({ ...p, meta_description: e.target.value }))} className="mt-1" /></div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-lg font-semibold">FAQ Categories</Label>
                    <Button onClick={addFAQCategory} size="sm" variant="outline"><Plus className="w-4 h-4 mr-1" /> Add Category</Button>
                  </div>

                  {faqContent.categories.map((cat, catIdx) => (
                    <div key={catIdx} className="mb-6 p-4 bg-zinc-50 rounded-lg border">
                      <div className="flex items-center gap-3 mb-4">
                        <Input value={cat.category} onChange={(e) => updateFAQCategory(catIdx, e.target.value)} className="font-semibold" placeholder="Category Name" />
                        <Button onClick={() => removeFAQCategory(catIdx)} size="sm" variant="ghost" className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                      </div>

                      <div className="space-y-3 ml-4">
                        {cat.questions.map((q, qIdx) => (
                          <div key={qIdx} className="p-3 bg-white rounded border">
                            <div className="flex gap-2 items-start mb-2">
                              <Input placeholder="Question" value={q.q} onChange={(e) => updateFAQQuestion(catIdx, qIdx, 'q', e.target.value)} className="flex-1" />
                              <Button onClick={() => removeFAQQuestion(catIdx, qIdx)} size="sm" variant="ghost" className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                            </div>
                            <Textarea placeholder="Answer" value={q.a} onChange={(e) => updateFAQQuestion(catIdx, qIdx, 'a', e.target.value)} rows={2} />
                          </div>
                        ))}
                        <Button onClick={() => addFAQQuestion(catIdx)} size="sm" variant="outline" className="w-full"><Plus className="w-4 h-4 mr-1" /> Add Question</Button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div><Label>CTA Title</Label><Input value={faqContent.cta_title} onChange={(e) => setFaqContent(p => ({ ...p, cta_title: e.target.value }))} className="mt-1" /></div>
                  <div><Label>CTA Subtitle</Label><Input value={faqContent.cta_subtitle} onChange={(e) => setFaqContent(p => ({ ...p, cta_subtitle: e.target.value }))} className="mt-1" /></div>
                  <div><Label>CTA Button Text</Label><Input value={faqContent.cta_button_text} onChange={(e) => setFaqContent(p => ({ ...p, cta_button_text: e.target.value }))} className="mt-1" /></div>
                </div>

                <Button onClick={handleSaveFAQ} disabled={loading} className="bg-[#064E3B] hover:bg-[#065F46]">
                  <Save className="w-4 h-4 mr-2" />{loading ? 'Saving...' : 'Save FAQ Page'}
                </Button>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPages;
